import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import IconLock from "images/lock.svg";
import {
  boostTiers,
  LEAD_SOURCES,
} from "@tomocredit/common-fe/consts";
import {
  TierInfoSection,
  CheckoutFooter,
  CheckoutPaymentSection,
} from "@tomocredit/common-fe/components";
import { formatCurrency, trackSubscribe } from "@tomocredit/common-fe/utils";
import * as braze from "@braze/web-sdk";
import { usePopup } from "components/common/wrappers/withPopup";

const CheckoutForm = ({
  email: emailPrefilled,
  productTier = boostTiers.BASIC,
  productTerm = "monthly",
  innerRef,
  errorMessageRef,
  belowMd,
  referralCode,
  promotionCredit,
  setPromotionCredit,
  promotionPercentOff,
  setPromotionPercentOff,
}) => {
  const { hideExitPopup } = usePopup();

  const [email, setEmail] = useState(emailPrefilled || "");
  const [referral, setReferral] = useState(referralCode || "");
  const history = useHistory();

  const onPaymentSucceed = (data) => {
    hideExitPopup();
    const {
      invoiceId,
      amount,
      paymentSource,
      code,
      fromSoftTerminated,
    } = data || {};

    const formattedAmount = amount ? Number(formatCurrency(amount / 100, {
      currencyDisplay: "none",
      digits: 2,
    })) : 0;

    const analyticsPayload = {
      email,
      tier: productTier,
      boostTerm: productTerm,
      amount: formattedAmount,
      paymentSource,
      price: formattedAmount,
      value: formattedAmount,
      currency: "USD",
      items: [
        {
          item_id: `Subscribe_${productTier}`,
          quantity: 1,
          price: formattedAmount,
        },
      ],
      leadSource: LEAD_SOURCES.LANDING_BOOST_CHECKOUT,
    };

    trackSubscribe(analyticsPayload);

    window.gtag?.("event", "purchase", analyticsPayload);
    window.gtag?.("event", `subscribe_${productTier}_${productTerm}`.toLowerCase(), analyticsPayload);
    window.gtag?.("event", "boost_checkout_payment_verified", analyticsPayload);
    if (fromSoftTerminated) {
      window.gtag?.("event", "Subscribe_from_soft_terminated", analyticsPayload);
    }

    window.uetq?.push("event", "subscribe", {
      event_label: "subscribe",
      event_category: "subscribe",
      contact: email,
    });

    window.flextrack?.track?.({
      order_number: invoiceId,
      order_amount: amount / 100,
      order_item: [
        {
          sku: `${productTier.toUpperCase()}_${productTerm.toUpperCase()}`,
          amount: amount / 100,
          quantity: 1,
        },
      ],
    });

    window.mbc('track', { mbd: invoiceId });

    // Then handle final redirect
    // using window.location.assign because we need to redirect user to /dashboard app
    if (code) {
      window.location.assign(
        `${process.env.REACT_APP_DOMAIN}/dashboard/boost-checkout-redirect?email=${encodeURIComponent(
          email,
        )}&code=${code}`,
      );
    }
  };

  const onPaymentFailed = (data) => {
    const {
      amount,
      paymentSource,
      errorReason = "Unknown error",
    } = data || {};

    const formattedAmount = amount ? Number(formatCurrency(amount / 100, {
      currencyDisplay: "none",
      digits: 2,
    })) : 0;

    const analyticsPayload = {
      email,
      tier: productTier,
      boostTerm: productTerm,
      amount: formattedAmount,
      paymentSource,
      errorReason,
      leadSource: LEAD_SOURCES.LANDING_BOOST_CHECKOUT,
    };
    window.gtag?.("event", "boost_checkout_failed", analyticsPayload);
  };

  const onPaymentInitiated = (data) => {
    braze.logCustomEvent("checkout_form_submitted", {
      ...data,
    });
  };

  return (
    <div
      className={`h-full text-white mx-10 ${belowMd ? "max-w-sm" : ""}`}
      ref={innerRef}
    >
      <div className="mt-5 flex justify-center items-center flex-col md:flex-row">
        <div className="ml-10 self-start md:self-center">
          <button
            type="button"
            className="text-sm font-semibold text-left hover:underline focus:outline-none pml-4"
            onClick={() => {
              const urlParams = new URLSearchParams(window.location.search);
              history.push(`/boost-pricing?${urlParams.toString()}`);
            }}
          >
            ← Back
          </button>
        </div>
        <div
          className="mt-3 md:mt-0 font-bold mx-auto self-center"
          style={{ fontSize: 40 }}
        >
          Checkout
        </div>
      </div>
      <div className={`mt-2 md:mt-10 ${!belowMd && "flex justify-between"}`}>
        <div className="xl:bg-black xl:bg-opacity-75 xl:rounded-lg xl:h-full">
          <TierInfoSection
            productTerm={productTerm}
            productTier={productTier}
            promotionCredit={promotionCredit}
            promotionPercentOff={promotionPercentOff}
            belowMd={belowMd}
          />
        </div>
        <div className={`flex flex-col ${belowMd ? "mt-10" : "max-w-md ml-10"}`}>
          {!belowMd && (
            <div className="mb-2 flex items-center whitespace-no-wrap">
              <img src={IconLock} alt="" className="inline mr-2" />
              <span className="text-sm">Guaranteed safe & secure checkout.</span>
            </div>
          )}
          <CheckoutPaymentSection
            email={email}
            setEmail={setEmail}
            showEmailInput
            productTerm={productTerm}
            productTier={productTier}
            errorMessageRef={errorMessageRef}
            setReferral={setReferral}
            referral={referral}
            promotionCredit={promotionCredit}
            promotionPercentOff={promotionPercentOff}
            setPromotionCredit={setPromotionCredit}
            setPromotionPercentOff={setPromotionPercentOff}
            onPaymentSucceed={onPaymentSucceed}
            onPaymentInitiated={onPaymentInitiated}
            onPaymentFailed={onPaymentFailed}
          />
        </div>
      </div>
      <CheckoutFooter belowMd={belowMd} />
    </div>
  );
};

export default CheckoutForm;
